import React from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { Constrain, Stack, Text, Button, useBreakpoint } from '@moda/om';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { hrefFor } from '../../routers';
import { useBagVisitTracking } from '../../hooks/useBagVisitTracking';
import { useCart, useCartItems } from '../../hooks/useCart';
import { usePreferences } from '../../hooks/usePreferences';
import { useSavedItems } from '../../hooks/useSavedItems';
import { useUser } from '../../hooks/useUser';
import { HolidayDelayContainer } from '../../components/ProductDetails/HolidayDelayContainer/HolidayDelayContainer';
import { LineItemList } from '../../components/LineItemList';
import { Loading } from '../../components/Loading';
import { PageMeta } from '../../components/PageMeta';
import { RewardJoin } from '../../components/Rewards';
import { ShoppingBagNotifications } from '../../components/ShoppingBagNotifications';
import { ShoppingBagPaymentSummary } from '../../components/PaymentSummary';
import { StickyCheckoutSection } from '../../components/StickyCheckoutSection';
import { ShoppingBagPageSavedItems } from './ShoppingBagPageSavedItems';

import './ShoppingBagPage.scss';

export const ShoppingBagPage: React.FC = () => {
  const {
    loading: loadingCart,
    error: cartError,
    cart: {
      attributes: {
        paymentSummary: { payToday }
      }
    }
  } = useCart();

  useBagVisitTracking();

  const { cartItems, availableNowItems, preorderItems, unavailableItems, canProceedToCheckout } =
    useCartItems();
  const { savedItems, loading: loadingSavedItems, error: savedItemsError } = useSavedItems();
  const { vertical } = usePreferences();
  const {
    user: { isLoggedIn }
  } = useUser();
  const location = useLocation();

  const isMobile = useBreakpoint({ lt: 'sm' }).matches;

  if (cartError) throwQueryError(cartError);
  if (savedItemsError) throwQueryError(savedItemsError);
  if (loadingCart || loadingSavedItems) return <Loading />;
  return (
    <>
      <HolidayDelayContainer />
      <PageMeta title="Shopping Bag" />

      <Constrain
        className={classNames('ShoppingBagPage', {
          'ShoppingBagPage--sticky-checkout-button': isMobile
        })}
      >
        {cartItems.length === 0 &&
          (!isLoggedIn ? (
            <Stack className="ShoppingBagPage__empty" space={6} alignItems="center">
              <Text as="h1" treatment="h3" family="serif">
                Shopping Bag
              </Text>
              <Stack space={4}>
                <Text treatment="h5" color="cement">
                  Do you have items in your bag?
                </Text>
                <Button to={`${hrefFor.RegisterPage()}?redirectTo=${location.pathname}`}>
                  SIGN IN TO SEE THEM
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack className="ShoppingBagPage__empty" space={4}>
              <Text treatment="body1" color="cement">
                {savedItems.length > 0
                  ? 'Your shopping bag is empty. To buy, move items from Saved For Later into your bag.'
                  : 'You have no items in your shopping bag.'}
              </Text>
              <Button
                to={hrefFor.ProductCategoryListingPage({
                  vertical,
                  section: 'new',
                  category: 'clothing'
                })}
              >
                Continue Shopping
              </Button>
            </Stack>
          ))}

        {(cartItems.length > 0 || savedItems.length > 0) && (
          <div className="ShoppingBagPage__main">
            <div className="ShoppingBagPage__list">
              {cartItems.length > 0 && (
                <Stack space={4}>
                  <Text as="h1" family="serif" treatment="h4">
                    Shopping Bag
                  </Text>
                  <ShoppingBagNotifications />
                  {availableNowItems.length > 0 && (
                    <LineItemList title="Available Now" items={availableNowItems} />
                  )}
                  {preorderItems.length > 0 && (
                    <LineItemList title="Preorder" items={preorderItems} />
                  )}
                  {unavailableItems.length > 0 && (
                    <LineItemList title="Unavailable" items={unavailableItems} />
                  )}
                </Stack>
              )}

              <ShoppingBagPageSavedItems savedItems={savedItems} />
            </div>

            {cartItems.length > 0 && (
              <Stack className="ShoppingBagPage__summary" data-testid="sb-summary" space={4}>
                <ShoppingBagPaymentSummary />

                <RewardJoin />
              </Stack>
            )}
          </div>
        )}
        {isMobile && cartItems.length !== 0 && (
          <StickyCheckoutSection
            payToday={payToday}
            to={hrefFor.CheckoutPage()}
            disabled={canProceedToCheckout}
          />
        )}
      </Constrain>
    </>
  );
};
